import { Menu } from '@mui/icons-material';
import { AppBar, Box, IconButton, Skeleton, Toolbar } from '@mui/material';
import { useSetAtom } from 'jotai';
import { Suspense, useState } from 'react';

import axleLogoSrc from '../../../assets/logo.svg';
import { useCurrentOrganization } from '../../../fetching/api/organization';
import { APP_BAR_Z_INDEX } from '../../../theme';
import { ReturnComponent } from '../../../utils/reactTypes';
import { appBarHeightMobile, isGlobalDrawerOpenAtom } from '../lib/constants';

const MobileAppBarLogo = (): ReturnComponent => {
    const organization = useCurrentOrganization();
    const [isOrgLogoLoaded, setIsOrgLogoLoaded] = useState(false);
    const hasOrgLogo = !!organization?.logoUrl;

    // If no org logo, just show Axle logo
    if (!hasOrgLogo) {
        return (
            <img
                src={axleLogoSrc}
                alt='Axle Health logo'
                style={{
                    maxHeight: '32px',
                    maxWidth: '200px',
                }}
            />
        );
    }

    // Otherwise show Axle logo | divider | org logo
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                maxHeight: '32px',
            }}
        >
            {/* Axle logo on the left */}
            <img
                src={axleLogoSrc}
                alt='Axle Health logo'
                style={{
                    maxHeight: '32px',
                    maxWidth: '80px',
                }}
            />

            {/* Vertical divider */}
            <Box
                sx={{
                    width: '1px',
                    height: '24px',
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                }}
            />

            {/* Org logo on the right */}
            <Box
                sx={{
                    maxHeight: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                }}
            >
                {!isOrgLogoLoaded && (
                    <Skeleton
                        variant='rectangular'
                        width='80px'
                        height='24px'
                        sx={{
                            borderRadius: 1,
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        }}
                    />
                )}
                {organization.logoUrl && (
                    <img
                        src={organization.logoUrl}
                        alt='Organization logo'
                        onLoad={() => setIsOrgLogoLoaded(true)}
                        style={{
                            maxHeight: '32px',
                            maxWidth: '120px',
                            opacity: isOrgLogoLoaded ? 1 : 0,
                            position: isOrgLogoLoaded ? 'static' : 'absolute',
                            transition: 'opacity 0.2s ease-in-out',
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

const MobileLogoSkeleton = (): ReturnComponent => (
    <Skeleton
        variant='rectangular'
        width='120px'
        height='24px'
        sx={{
            borderRadius: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
        }}
    />
);

export const GlobalMobileAppBar = (): ReturnComponent => {
    const setIsDrawerOpen = useSetAtom(isGlobalDrawerOpenAtom);
    const toggleDrawer = () => setIsDrawerOpen((isOpen) => !isOpen);

    return (
        <AppBar
            position='fixed'
            sx={{
                zIndex: APP_BAR_Z_INDEX,
                backgroundColor: '#ffffff',
                height: appBarHeightMobile,
            }}
        >
            <Toolbar
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: appBarHeightMobile,
                    px: 2,
                    gap: 2,
                }}
            >
                <IconButton
                    onClick={toggleDrawer}
                    edge='start'
                    sx={{
                        color: '#000000',
                        p: 1,
                    }}
                >
                    <Menu />
                </IconButton>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Suspense fallback={<MobileLogoSkeleton />}>
                        <MobileAppBarLogo />
                    </Suspense>
                </Box>
            </Toolbar>
        </AppBar>
    );
};
